import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import { deleteBlob, saveBlob } from "./blob-store";

/**
 * Snapshot meta-list storage + helpers shared by the WS4 facilitation
 * tools (Handshake Board, Stress Test). The full-resolution PNG lives
 * in the dedicated blob store; only lightweight meta (thumbnail
 * data-URL, timestamps, blob key, optional structured state) is
 * stashed in the main `idb-keyval` keyspace alongside the rest of
 * the workshop state.
 *
 * History is per `(kind, workshopId)`. Auto-snapshots are evicted FIFO
 * past `AUTO_SNAPSHOT_CAP` so the IDB store can't bloat across an
 * unattended afternoon — manual snapshots are sacred and never
 * evicted, matching the "Save snapshot" mental model where the user
 * explicitly asked to keep this version forever.
 */

export type SnapshotKind = "handshake" | "stress-test";

export type SnapshotSource = "manual" | "auto";

export type SnapshotMeta = {
  id: string;
  kind: SnapshotKind;
  workshopId: string;
  createdAt: number;
  /** Small JPEG data-URL (~120 px wide) for the history grid tile. */
  thumbnailDataUrl: string;
  /** Pointer into `src/lib/blob-store.ts` for the full-res PNG. */
  blobKey: string;
  source: SnapshotSource;
  /**
   * Optional structured state alongside the PNG. Tools that can't
   * reverse a render (e.g. StressTest needs card → zone mappings)
   * stash their state here so `restore()` can rehydrate the board.
   */
  stateJson?: string;
};

/** Cap auto-saves per (kind, workshopId). Manual entries are exempt. */
export const AUTO_SNAPSHOT_CAP = 10;

/** Target width for history thumbnails, in CSS pixels. */
const THUMB_WIDTH = 120;

/** JPEG quality for thumbnails — small enough to keep meta-list cheap. */
const THUMB_QUALITY = 0.78;

function metaKey(kind: SnapshotKind, workshopId: string): string {
  return `xaes-snapshots:${kind}:${workshopId}`;
}

/**
 * Build the blob-store key for a snapshot. Mirrors the convention
 * documented in `blob-store.ts`. Centralised so the prefix scan in
 * any future orphan-sweep tooling stays correct.
 */
export function snapshotBlobKey(
  kind: SnapshotKind,
  workshopId: string,
  id: string,
): string {
  return `snap:${kind}:${workshopId}:${id}`;
}

function isSnapshotMeta(value: unknown): value is SnapshotMeta {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    (v.kind === "handshake" || v.kind === "stress-test") &&
    typeof v.workshopId === "string" &&
    typeof v.createdAt === "number" &&
    typeof v.thumbnailDataUrl === "string" &&
    typeof v.blobKey === "string" &&
    (v.source === "manual" || v.source === "auto")
  );
}

export async function loadSnapshotMeta(
  kind: SnapshotKind,
  workshopId: string,
): Promise<SnapshotMeta[]> {
  try {
    const raw = await idbGet<unknown>(metaKey(kind, workshopId));
    if (!Array.isArray(raw)) return [];
    return raw.filter(isSnapshotMeta);
  } catch {
    return [];
  }
}

async function saveSnapshotMeta(
  kind: SnapshotKind,
  workshopId: string,
  list: SnapshotMeta[],
): Promise<void> {
  try {
    await idbSet(metaKey(kind, workshopId), list);
  } catch {
    // Best-effort persistence — see other features for the same posture.
  }
}

export function makeSnapshotId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `snap-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Downscale a PNG `Blob` to a JPEG data-URL ~`THUMB_WIDTH` px wide.
 * Returns an empty string on any failure (callers can choose to
 * fall back to an icon glyph rather than block snapshot creation).
 */
export async function blobToThumbnail(blob: Blob): Promise<string> {
  try {
    const bitmap = await createImageBitmap(blob);
    const ratio = bitmap.height / bitmap.width || 1;
    const w = Math.min(THUMB_WIDTH, bitmap.width);
    const h = Math.max(1, Math.round(w * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    return canvas.toDataURL("image/jpeg", THUMB_QUALITY);
  } catch {
    return "";
  }
}

export type AppendSnapshotInput = {
  kind: SnapshotKind;
  workshopId: string;
  blob: Blob;
  source: SnapshotSource;
  stateJson?: string;
};

/**
 * Capture-and-persist orchestration: writes the blob, builds the
 * meta, prepends it to the per-(kind, workshopId) list, and evicts
 * stale auto-snapshots past the cap (deleting their blobs too).
 *
 * Returns the newly-appended meta so callers (e.g. the manual "Save
 * to Asset Locker" button) can chain it into other side-effects.
 */
export async function appendSnapshot(
  input: AppendSnapshotInput,
): Promise<{ meta: SnapshotMeta; list: SnapshotMeta[] }> {
  const id = makeSnapshotId();
  const blobKey = snapshotBlobKey(input.kind, input.workshopId, id);
  await saveBlob(blobKey, input.blob);

  const thumbnailDataUrl = await blobToThumbnail(input.blob);
  const meta: SnapshotMeta = {
    id,
    kind: input.kind,
    workshopId: input.workshopId,
    createdAt: Date.now(),
    thumbnailDataUrl,
    blobKey,
    source: input.source,
    stateJson: input.stateJson,
  };

  const prior = await loadSnapshotMeta(input.kind, input.workshopId);
  // Newest first — UI grids render in display order without resorting.
  const next: SnapshotMeta[] = [meta, ...prior];

  // Evict auto-saves past the cap. Manual entries are sacred and
  // pass through the filter unchanged. We walk newest → oldest so
  // the *youngest* AUTO_SNAPSHOT_CAP auto entries survive.
  let autoSeen = 0;
  const kept: SnapshotMeta[] = [];
  const evicted: SnapshotMeta[] = [];
  for (const m of next) {
    if (m.source === "auto") {
      autoSeen += 1;
      if (autoSeen > AUTO_SNAPSHOT_CAP) {
        evicted.push(m);
        continue;
      }
    }
    kept.push(m);
  }

  await saveSnapshotMeta(input.kind, input.workshopId, kept);
  // Fire-and-forget blob deletion for evicted entries.
  for (const m of evicted) {
    void deleteBlob(m.blobKey);
  }

  return { meta, list: kept };
}

export async function removeSnapshot(
  kind: SnapshotKind,
  workshopId: string,
  id: string,
): Promise<SnapshotMeta[]> {
  const list = await loadSnapshotMeta(kind, workshopId);
  const doomed = list.find((m) => m.id === id);
  const next = list.filter((m) => m.id !== id);
  await saveSnapshotMeta(kind, workshopId, next);
  if (doomed) {
    void deleteBlob(doomed.blobKey);
  }
  return next;
}
